/**
 * gameService.js
 * Central helper module for all game lifecycle CRUD operations.
 */

import { doc, setDoc, updateDoc, deleteDoc, getDoc, collection, query, where, orderBy, getDocs, onSnapshot, limit, runTransaction, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db } from './firebase';
import { supabase } from './supabase';

// ───────────────────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────────────────

const newSessionDoc = () => ({
    createdAt: new Date().toISOString(),
    status: 'active',
    playerCount: 10,
    tableSeats: {},
    buyins: [],
    expenses: [],
    collected: [],
    attachedRestaurants: [],
    confirmationType: 'simple', 
    autoWaitlist: true,
    autoSettle: false,
    registered_players: {}, 
});

const addDays = (isoString, days) => {
    const d = new Date(isoString);
    d.setDate(d.getDate() + days);
    return d.toISOString();
};

// ───────────────────────────────────────────────────────────────────────────
// createGame
// ───────────────────────────────────────────────────────────────────────────

export const createGame = async (hostId, { stakes, minBuyIn, startTime, isRecurring = 'One-time', attachedRestaurants = [], confirmationType = 'simple', capacity = '1 Table', variant = 'No Limit Holdem', table1Config, table2Config, autoWaitlist = true, autoSettle = false }) => {
    const _create = async (st) => {
        const gameId = `game_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
        const ledgerId = `session_${gameId}`;
        const ledgerId2 = capacity === '2 Tables' ? `session_${gameId}_t2` : null;

        const ledgerRef = doc(db, `hosts/${hostId}/sessions/${ledgerId}`);
        await setDoc(ledgerRef, {
            ...newSessionDoc(),
            gameId,
            stakes: table1Config?.stakes || stakes,
            minBuyIn: Number(table1Config?.minBuyIn || minBuyIn) || 0,
            startTime: st,
            attachedRestaurants,
            confirmationType,
            capacity: '1 Table',
            variant: table1Config?.variant || variant,
            tableIndex: 1,
            autoWaitlist,
            autoSettle
        });

        if (ledgerId2) {
            const ledgerRef2 = doc(db, `hosts/${hostId}/sessions/${ledgerId2}`);
            await setDoc(ledgerRef2, {
                ...newSessionDoc(),
                gameId,
                stakes: table2Config?.stakes || stakes,
                minBuyIn: Number(table2Config?.minBuyIn || minBuyIn) || 0,
                startTime: st,
                attachedRestaurants,
                confirmationType,
                capacity: '1 Table',
                variant: table2Config?.variant || variant,
                tableIndex: 2
            });
        }

        const gameRef = doc(db, `hosts/${hostId}/games/${gameId}`);
        await setDoc(gameRef, {
            id: gameId, 
            stakes: stakes || '', 
            minBuyIn: Number(minBuyIn) || 0,
            startTime: st, startedAt: null, endedAt: null, isRecurring,
            capacity, variant,
            status: 'upcoming', ledgerId, ledgerId2, createdAt: new Date().toISOString(),
            attachedRestaurants, confirmationType,
            autoWaitlist,
            autoSettle,
            table1Config: table1Config || null,
            table2Config: table2Config || null
        });
        return gameId;
    };

    const firstId = await _create(startTime);
    
    if (isRecurring === 'Every Week') {
        for (let i = 1; i <= 3; i++) {
            await _create(addDays(startTime, i * 7));
        }
    }
    
    return firstId;
};

/**
 * Mid-session: Scaling from 1 table to 2 tables.
 */
export const scaleGameToTwoTables = async (hostId, gameId, table2Config) => {
    const gameRef = doc(db, `hosts/${hostId}/games/${gameId}`);
    const gameSnap = await getDoc(gameRef);
    if (!gameSnap.exists()) throw new Error("Game not found");
    const gameData = gameSnap.data();

    const ledgerId2 = `session_${gameId}_t2`;
    const ledgerRef2 = doc(db, `hosts/${hostId}/sessions/${ledgerId2}`);

    await setDoc(ledgerRef2, {
        ...newSessionDoc(),
        gameId,
        stakes: table2Config.stakes,
        minBuyIn: Number(table2Config.minBuyIn) || 0,
        startTime: gameData.startTime,
        attachedRestaurants: gameData.attachedRestaurants || [],
        confirmationType: gameData.confirmationType || 'simple',
        capacity: '1 Table',
        variant: table2Config.variant,
        tableIndex: 2
    });

    await updateDoc(gameRef, {
        capacity: '2 Tables',
        ledgerId2,
        table2Config
    });

    return ledgerId2;
};

// ───────────────────────────────────────────────────────────────────────────
// startGame — marks a game as ongoing
// ───────────────────────────────────────────────────────────────────────────

export const startGame = async (hostId, gameId) => {
    const gameRef = doc(db, `hosts/${hostId}/games/${gameId}`);
    const gameSnap = await getDoc(gameRef);
    if (!gameSnap.exists()) return;
    const gameData = gameSnap.data();

    const now = new Date();
    const plannedStart = new Date(gameData.startTime);
    let startedAt = now.toISOString();

    if (plannedStart < now) {
        startedAt = gameData.startTime;
    }

    await updateDoc(gameRef, { status: 'ongoing', startedAt });
    // WhatsApp notifications now run through the Cloud Functions agent bridge
    // (functions/whatsapp.js), not a browser-side webhook to a dead host.
};

// ───────────────────────────────────────────────────────────────────────────
// endGame — marks a game as completed and updates player analytics
// ───────────────────────────────────────────────────────────────────────────

export const endGame = async (hostId, gameId) => {
    const gameRef = doc(db, `hosts/${hostId}/games/${gameId}`);
    const now = new Date().toISOString();
    
    await runTransaction(db, async (transaction) => {
        const gameSnap = await transaction.get(gameRef);
        if (!gameSnap.exists()) throw new Error("Game not found");
        
        const gameData = gameSnap.data();
        const ledgerId = gameData.ledgerId;
        const ledgerId2 = gameData.ledgerId2;

        const sessionRef = doc(db, `hosts/${hostId}/sessions/${ledgerId}`);
        const sessionSnap = await transaction.get(sessionRef);
        if (!sessionSnap.exists()) throw new Error("Session not found");
        
        let sessionSnap2 = null;
        if (ledgerId2) {
            const sessionRef2 = doc(db, `hosts/${hostId}/sessions/${ledgerId2}`);
            sessionSnap2 = await transaction.get(sessionRef2);
        }

        const sessionData = sessionSnap.data();
        const sessionData2 = sessionSnap2?.exists() ? sessionSnap2.data() : null;
        
        const buyins = [...(sessionData.buyins || [])];
        if (sessionData2) buyins.push(...(sessionData2.buyins || []));

        const cashouts = [...(sessionData.cashouts || [])];
        if (sessionData2) cashouts.push(...(sessionData2.cashouts || []));

        const collected = [...(sessionData.collected || [])];
        if (sessionData2) collected.push(...(sessionData2.collected || []));

        const expenses = [...(sessionData.expenses || [])];
        if (sessionData2) expenses.push(...(sessionData2.expenses || []));
        
        const totalRake = collected.reduce((s, c) => s + (Number(c.amount) || 0), 0);
        const totalBuyinsSum = buyins.reduce((s, b) => s + (Number(b.amount) || 0), 0);
        
        const playerIds = [...new Set(buyins.map(b => b.playerId))];
        const leaderboard = [];

        for (const pid of playerIds) {
            const playerRef = doc(db, `hosts/${hostId}/players/${pid}`);
            const playerSnap = await transaction.get(playerRef);
            if (!playerSnap.exists()) continue;
            
            const pBuyins = buyins.filter(b => b.playerId === pid);
            const pCashouts = cashouts.filter(c => c.playerId === pid);
            
            const totalPBuyins = pBuyins.reduce((s, b) => s + (Number(b.amount) || 0), 0);
            const totalPCashouts = pCashouts.reduce((s, c) => s + (Number(c.amount) || 0), 0);
            const netResult = totalPCashouts - totalPBuyins;
            
            const firstBuyinTime = pBuyins.length > 0 ? Math.min(...pBuyins.map(b => new Date(b.timestamp).getTime())) : Date.now();
            const lastCashoutTime = pCashouts.length > 0 ? Math.max(...pCashouts.map(c => new Date(c.timestamp).getTime())) : Date.now();
            const durationMinutes = Math.max(0, (lastCashoutTime - firstBuyinTime) / (1000 * 60));
            
            const pData = playerSnap.data();
            const newTotalPL = (pData.totalProfitLoss || 0) + netResult;
            const newTotalHours = (pData.totalHoursPlayed || 0) + (durationMinutes / 60);
            
            transaction.update(playerRef, {
                totalProfitLoss: newTotalPL,
                totalBuyIns: (pData.totalBuyIns || 0) + totalPBuyins,
                totalHoursPlayed: newTotalHours,
                sessionsPlayed: (pData.sessionsPlayed || 0) + 1,
                hourlyWinRate: newTotalHours > 0 ? newTotalPL / newTotalHours : 0,
                lifetimeValue: (pData.lifetimeValue || 0) + (totalRake / playerIds.length),
                lastPlayedDate: serverTimestamp(),
                registrationStatus: 'registered'
            });
            
            leaderboard.push({
                name: pData.name,
                net: netResult,
                buyins: totalPBuyins
            });
        }
        
        const summaryReport = {
            totalRake,
            totalBuyins: totalBuyinsSum,
            totalDuration: (new Date(now) - new Date(gameData.startedAt || now)) / (1000 * 60),
            leaderboard: leaderboard.sort((a, b) => b.net - a.net)
        };
        
        transaction.update(gameRef, {
            status: 'completed',
            endedAt: now,
            summaryReport
        });
        
        transaction.update(sessionRef, {
            status: 'completed',
            endedAt: now
        });

        if (ledgerId2) {
            const sessionRef2 = doc(db, `hosts/${hostId}/sessions/${ledgerId2}`);
            transaction.update(sessionRef2, {
                status: 'completed',
                endedAt: now
            });
        }
        
        const historyRef = doc(db, `hosts/${hostId}/session_history/${ledgerId}`);
        transaction.set(historyRef, {
            ...sessionData,
            status: 'completed',
            endedAt: now,
            summaryReport
        });

        if (ledgerId2 && sessionData2) {
            const historyRef2 = doc(db, `hosts/${hostId}/session_history/${ledgerId2}`);
            transaction.set(historyRef2, {
                ...sessionData2,
                status: 'completed',
                endedAt: now,
                summaryReport
            });
        }

        if (gameData.isRecurring === 'Every Week') {
            const nextTime = addDays(gameData.startTime, 28);
            const oneYearFromNow = new Date();
            oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);

            if (new Date(nextTime) <= oneYearFromNow) {
                setTimeout(() => {
                    createGame(hostId, {
                        stakes: gameData.stakes,
                        minBuyIn: gameData.minBuyIn,
                        startTime: nextTime,
                        isRecurring: gameData.isRecurring,
                        attachedRestaurants: gameData.attachedRestaurants || [],
                        confirmationType: gameData.confirmationType || 'simple',
                        capacity: gameData.capacity || '1 Table',
                        variant: gameData.variant || 'No Limit Holdem'
                    });
                }, 2000);
            }
        }
    });
};

export const getGames = async (hostId) => {
    const q = query(
        collection(db, `hosts/${hostId}/games`),
        orderBy('startTime', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getCompletedGames = async (hostId, limitN) => {
    const constraints = [
        where('status', '==', 'completed'),
        orderBy('startTime', 'desc'),
    ];
    if (limitN) {
        constraints.push(limit(limitN));
    }
    const q = query(collection(db, `hosts/${hostId}/games`), ...constraints);
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const subscribeGames = (hostId, callback) => {
    const q = query(
        collection(db, `hosts/${hostId}/games`),
        orderBy('startTime', 'asc')
    );
    return onSnapshot(q, (snapshot) => {
        const games = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        callback(games);
    });
};

export const deleteGame = async (hostId, gameId) => {
    return deleteMultipleSessions(hostId, [gameId]);
};

/**
 * Atomic batch deletion for sessions across Firestore and Supabase.
 */
export const deleteMultipleSessions = async (hostId, gameIds) => {
    if (!hostId || !gameIds?.length) return;

    try {
        const batch = writeBatch(db);

        gameIds.forEach(id => {
            // 1. Delete Game Document
            const gameRef = doc(db, `hosts/${hostId}/games/${id}`);
            batch.delete(gameRef);

            // 2. Delete Associated Ledgers (Conventional naming)
            const ledgerId = `session_${id}`;
            const ledgerId2 = `session_${id}_t2`;
            
            batch.delete(doc(db, `hosts/${hostId}/sessions/${ledgerId}`));
            batch.delete(doc(db, `hosts/${hostId}/sessions/${ledgerId2}`));
        });

        // Execute Firestore Batch
        await batch.commit();

        // 3. Supabase RPC Sync (Agent 1: Atomic Cleanup)
        const { error } = await supabase.rpc('delete_multiple_sessions', {
            p_session_ids: gameIds
        });

        if (error) {
            console.error('[Supabase Deletion Error]', error);
        }
    } catch (err) {
        console.error('[Batch Deletion Failed]', err);
        throw err;
    }
};

/**
 * Duplicates one or more sessions as new "Upcoming" sessions.
 */
export const duplicateMultipleSessions = async (hostId, gameIds) => {
    if (!hostId || !gameIds?.length) return;
    const batch = writeBatch(db);
    
    try {
        const promises = gameIds.map(id => getDoc(doc(db, `hosts/${hostId}/games/${id}`)));
        const snaps = await Promise.all(promises);
        
        snaps.forEach(snap => {
            if (!snap.exists()) return;
            const data = snap.data();
            
            const newGameId = `game_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
            const newLedgerId = `session_${newGameId}`;
            const newLedgerId2 = data.capacity === '2 Tables' ? `session_${newGameId}_t2` : null;
            
            // 1. New Game Document
            const newGameRef = doc(db, `hosts/${hostId}/games/${newGameId}`);
            batch.set(newGameRef, {
                ...data,
                id: newGameId,
                status: 'upcoming',
                startedAt: null,
                endedAt: null,
                createdAt: new Date().toISOString(),
                ledgerId: newLedgerId,
                ledgerId2: newLedgerId2
            });
            
            // 2. New Ledger(s)
            const ledgerRef = doc(db, `hosts/${hostId}/sessions/${newLedgerId}`);
            batch.set(ledgerRef, {
                ...newSessionDoc(),
                gameId: newGameId,
                stakes: data.table1Config?.stakes || data.stakes,
                minBuyIn: Number(data.table1Config?.minBuyIn || data.minBuyIn) || 0,
                attachedRestaurants: data.attachedRestaurants || [],
                confirmationType: data.confirmationType || 'simple',
                capacity: '1 Table',
                variant: data.table1Config?.variant || data.variant,
                tableIndex: 1,
                autoWaitlist: data.autoWaitlist ?? true,
                autoSettle: data.autoSettle ?? false,
                startTime: data.startTime
            });
            
            if (newLedgerId2) {
                const ledgerRef2 = doc(db, `hosts/${hostId}/sessions/${newLedgerId2}`);
                batch.set(ledgerRef2, {
                    ...newSessionDoc(),
                    gameId: newGameId,
                    stakes: data.table2Config?.stakes || data.stakes,
                    minBuyIn: Number(data.table2Config?.minBuyIn || data.minBuyIn) || 0,
                    attachedRestaurants: data.attachedRestaurants || [],
                    confirmationType: data.confirmationType || 'simple',
                    capacity: '1 Table',
                    variant: data.table2Config?.variant || data.variant,
                    tableIndex: 2,
                    startTime: data.startTime
                });
            }
        });
        
        await batch.commit();
        return true;
    } catch (err) {
        console.error('[Duplicate Failed]', err);
        throw err;
    }
};
